import { usePortalValue } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * "Ring a number now" — the VoiceAgent's Outbound tab (plan-008 ST-UA, design §22.14, P13, DD8-26).
 *
 * The agent rings one number through the account's own SIP trunk, from one of its caller-ID numbers:
 * the number to ring, the number to call from, how long to let it ring, an optional reference for
 * History, and the sentence that says what it costs — billed talk time from when they answer.
 *
 * ── ONE KEY, ONE RING ──────────────────────────────────────────────────────────────────────
 * `startOutboundConversation` requires an Idempotency-Key and never rings twice for one key. The key
 * is made when the dialog opens and sent again on every press while WeTalk has not said what became
 * of the last one (no answer, a 5xx): a fresh key per press would turn a lost answer into a second
 * ring. Only when the API has DECIDED — a 4xx, every one of which says nothing was dialled — is the
 * next press given a new key, because the API records a decided refusal against its key and would
 * otherwise replay it (a 402 after a top-up) or refuse a corrected number as a different body. If
 * the member changes the number after an undecided press, the API refuses the key as reused
 * (`409 idempotency_key_reused_with_different_body`) and the dialog says to look in History first.
 *
 * ── CHECKED BEFORE IT IS SENT, REFUSED WHERE IT BELONGS ─────────────────────────────────────────
 * The number is read in the forms a Greek member writes (`69 1234 5678`, `+30 21 0123 4567`,
 * `0030…`) and judged by the domain's own destination rules (`destinationVerdict`, GR only, fail
 * closed) against the chosen trunk's countries before anything is sent. A refusal from the API is
 * printed at the field it names — the number, the caller-ID number, the ring time — and one that
 * names no field (the per-minute limit, today's ceiling, WeTalk's SIP edge not answering, a resting
 * model) above the buttons, in plain words. The per-minute limit, when its `Retry-After` came back, is
 * counted down there and holds Ring now for exactly that long (`../../numbers/dial-wait.ts`, plan-008
 * ST-RV2); a trunk the runtime would not dial through (`sip_trunk_unverified`) is said in the API's own
 * customer-safe sentence — not verified yet, or a host off the public internet.
 *
 * ── PROGRESS IS READ, NEVER ASSUMED ─────────────────────────────────────────────────────────────
 * Once started, the dialog polls `getOutboundConversation` until the conversation ends and says what
 * it reads: "Ringing…", "Answered · HD (G.722, 16 kHz)", "Busy", "No answer", "Could not be reached",
 * "Ended after 2 min 14 s". The codec is the one the SIP leg was observed to use — "Codec not
 * reported" when it was not, never a default — with the honest HD sentence (§22.10.2). End hangs up;
 * closing the dialog does not, and it says so.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { Button, Dialog, Field, Input, Select } from "@wetalk/ui";
import {
  REVIEWED_DESTINATION_PREFIX,
  destinationVerdict,
  fromWireNumber,
  type DestinationRefusalRule,
} from "@wetalk/domain";
import { useState, type ReactNode } from "react";

import "./ring-now-dialog.css";

import { apiErrorText, carriesEnvelope } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY } from "../../../api/query.js";
import type { OutboundConversationView } from "../../../api/slice/outbound-conversation.js";
import type { SipTrunkView } from "../../../api/slice/sip-trunk.js";
import { useTalkRates } from "../../../api/slice/talk-rate.js";
import { callerIdChoicesOf, outboundRateTextOf } from "../../campaigns/campaign-copy.js";
import { dialRateLimitedSentence, dialWaitOf, useDialWait } from "../../numbers/dial-wait.js";
import { HD_SENTENCE, codecWords, endReasonWords } from "../../numbers/sip-trunk-copy.js";
import { SIP_TRUNK_QUERY_KEY } from "../../numbers/sip-trunk-query.js";
import { durationWords } from "../../numbers/sip-trunk-test-call.js";

/** How often the dialog reads the conversation while it rings or runs. */
export const RING_PROGRESS_POLL_MS = 2_000;

/**
 * The ring time the form opens with, and its bounds — the product owner's P6 values (30 s, at most
 * 80 s), which the API applies as its own default and ceiling. A value the API's configured ceiling
 * refuses comes back at the Ring for field in the API's words; nothing here is a configuration value.
 */
export const RING_SECOND_DEFAULT = 30;
export const RING_SECOND_MIN = 5;
export const RING_SECOND_MAX = 80;

/** The most characters a reference holds (the API's `outbound_reference`). */
const REFERENCE_MAX = 128;

/** The design's sentence, verbatim (§22.14). */
export const RING_BILLING_SENTENCE = () => (portalText("operations", "this_is_billed_talk_time_from_when_they_answer_a2d18427"));

type RingField = "party_number" | "caller_id_phone_number_id" | "ring_second" | "reference";

/** Where a problem is printed: at one field, or above the buttons. */
export interface RingProblem {
  readonly field: RingField | null;
  readonly text: string;
}

/**
 * The number as E.164, from the forms a member writes: `+30 69 1234 5678`, `0030 69…`, or the ten
 * national digits (`69 1234 5678`, `210 123 4567` — Greece has no trunk prefix). Spaces, dots,
 * dashes and brackets are ignored. `null` when it is none of these.
 */
export function partyNumberOf(typed: string): string | null {
  const bare = typed.replace(/[\s.\-()]/g, "");
  if (/^\+\d{6,15}$/.test(bare)) return bare;
  if (/^00\d{6,15}$/.test(bare)) return `+${bare.slice(2)}`;
  if (/^\d{10}$/.test(bare)) {
    try {
      return fromWireNumber(bare, "national", "GR");
    } catch {
      return null;
    }
  }
  return null;
}

const DESTINATION_WORD: Readonly<Record<DestinationRefusalRule, string>> = {
  get not_e164() { return portalText("operations", "that_is_not_a_whole_phone_number_write_it_like_69_1234_567_1737dcfa"); },
  get country_unreviewed() { return portalText("operations", "wetalk_rings_greek_numbers_only_for_now_84f54d84"); },
  get country_not_allowed() { return portalText("operations", "your_sip_trunk_may_not_ring_that_country_the_countries_it_58dd92b9"); },
  get emergency_or_short() { return portalText("operations", "wetalk_never_rings_emergency_numbers_or_short_codes_965507f6"); },
  get premium_rate() { return portalText("operations", "wetalk_does_not_ring_premium_rate_or_personal_70_numbers_7bec8bd1"); },
};

/** The number's problem before anything is sent, or `null` when the domain's rules allow it. */
export function partyNumberProblem(typed: string, allowedCountry: readonly string[]): string | null {
  if (typed.trim() === "") return portalText("operations", "write_the_number_to_ring_ab99b839");
  const e164 = partyNumberOf(typed);
  if (e164 === null) return DESTINATION_WORD.not_e164;
  const verdict = destinationVerdict(e164, allowedCountry, REVIEWED_DESTINATION_PREFIX);
  return verdict.allowed ? null : DESTINATION_WORD[verdict.rule];
}

/** The ring time's problem, or `null` for a whole number of seconds inside the bounds. */
export function ringSecondProblem(typed: string): string | null {
  const second = Number(typed);
  if (!/^\d+$/.test(typed.trim()) || second < RING_SECOND_MIN || second > RING_SECOND_MAX) {
    return portalText("operations", "ring_for_a_whole_number_of_seconds_from_value_to_value_7eb7aae8", { v0: String(String(RING_SECOND_MIN)), v1: String(String(RING_SECOND_MAX)) });
  }
  return null;
}

const NOTHING_DIALLED = () => (portalText("operations", "nothing_was_dialled_597fcc72"));

/** A refused start, in plain words, at the field it names. */
export function ringRefusalOf(error: unknown): RingProblem {
  if (!(error instanceof ApiError)) {
    return {
      field: null,
      get text() { return portalText("operations", "wetalk_did_not_answer_so_this_screen_cannot_tell_whether_t_95b713f1"); },
    };
  }
  switch (error.code) {
    case "destination_refused":
      return {
        field: "party_number",
        get text() { return portalText("operations", "wetalk_does_not_ring_that_number_through_your_trunk_emerge_a5dcd7c5", { v0: String(NOTHING_DIALLED()) }); },
      };
    case "contact_suppressed":
      return { field: "party_number", text: apiErrorText(error, "") };
    case "caller_id_not_bound":
      return {
        field: "caller_id_phone_number_id",
        get text() { return portalText("operations", "that_number_is_not_a_caller_id_number_connected_to_this_ag_1439f833", { v0: String(NOTHING_DIALLED()) }); },
      };
    case "sip_trunk_unverified":
      // The API's sentence says which (ST-RV2): not verified yet, or a host the runtime will not dial.
      return {
        field: "caller_id_phone_number_id",
        get text() { return carriesEnvelope(error)
          ? apiErrorText(error, "")
          : portalText("operations", "this_number_s_sip_trunk_is_not_verified_yet_check_the_conn_ce67f0e2", { v0: String(NOTHING_DIALLED()) }); },
      };
    case "rate_limited": {
      const second = dialWaitOf(error);
      return { field: null, text: second === null ? apiErrorText(error, NOTHING_DIALLED()) : dialRateLimitedSentence(second) };
    }
    case "sip_trunk_disabled":
      return {
        field: "caller_id_phone_number_id",
        get text() { return portalText("operations", "this_number_s_sip_trunk_is_disabled_enable_it_on_numbers_s_ab0d29c4", { v0: String(NOTHING_DIALLED()) }); },
      };
    case "idempotency_key_reused_with_different_body":
      return {
        field: null,
        get text() { return portalText("operations", "wetalk_never_answered_your_last_ring_now_so_it_may_have_st_606cddb2"); },
      };
    case "validation_failed":
      if (error.field === "party_number") {
        return { field: "party_number", get text() { return portalText("operations", "write_the_number_in_international_form_like_30_69_1234_567_c4cb9f31"); } };
      }
      if (error.field === "ring_second") {
        return { field: "ring_second", get text() { return portalText("operations", "wetalk_refused_that_ring_time_value_c53b0157", { v0: String(apiErrorText(error, "")) }); } };
      }
      if (error.field === "reference") {
        return { field: "reference", get text() { return portalText("operations", "keep_the_reference_to_value_ordinary_characters_476d9896", { v0: String(String(REFERENCE_MAX)) }); } };
      }
      return { field: null, text: apiErrorText(error, NOTHING_DIALLED()) };
    default:
      // The per-minute limit, today's ceiling, WeTalk's SIP edge, a resting model, the balance: the
      // API's own sentences say the wait and that nothing was dialled.
      return { field: null, text: apiErrorText(error, NOTHING_DIALLED()) };
  }
}

/** Whether the API decided the press — nothing was dialled, so the next press may take a new key. */
function decided(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.code !== "idempotency_key_reused_with_different_body" &&
    error.code !== "idempotency_in_progress"
  );
}

/** How the conversation is going, in the design's plain words. */
export function ringProgressWords(one: OutboundConversationView): string {
  const codec = codecWords(one.sipLeg?.codec ?? null);
  switch (one.state) {
    case "dialing":
      return portalText("operations", "ringing_417814b1");
    case "in_progress":
      return portalText("operations", "answered_value_d7a4a73d", { v0: String(codec) });
    case "ended": {
      if (one.answeredAtText === null) {
        return one.endReason === null ? portalText("operations", "ended_before_it_was_answered_5d0ead1c") : endReasonWords(one.endReason);
      }
      const length = one.billedSecond === null ? "" : portalText("operations", "after_value_4b46dc65", { v0: String(durationWords(one.billedSecond)) });
      const reason = one.endReason === null ? "" : ` · ${endReasonWords(one.endReason)}`;
      return portalText("operations", "endedvaluevalue_value_46f34686", { v0: String(length), v1: String(reason), v2: String(codec) });
    }
  }
}

export interface RingNowDialogProps {
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
  /** The workspace's trunks; the dialog offers this agent's caller-ID numbers on verified ones. */
  readonly sipTrunk: readonly SipTrunkView[];
  readonly onClose: () => void;
  /** How often progress is read; the suites pass a short one. */
  readonly pollMs?: number;
}

function fieldError(problem: RingProblem | null, field: RingField): { readonly error?: string } {
  return problem !== null && problem.field === field ? { error: problem.text } : {};
}

export function RingNowDialog({
  voiceAgentId,
  voiceAgentName,
  sipTrunk,
  onClose,
  pollMs = RING_PROGRESS_POLL_MS,
}: RingNowDialogProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const choice = callerIdChoicesOf(sipTrunk, voiceAgentId);
  const [callerId, setCallerId] = useState(choice[0]?.phoneNumberId ?? "");
  const [partyNumber, setPartyNumber] = useState("");
  const [ringSecond, setRingSecond] = useState(String(RING_SECOND_DEFAULT));
  const [reference, setReference] = useState("");
  const [problem, setProblem] = usePortalValue<RingProblem | null>(null);
  // One key per dialog; replaced only after the API decided a press (see the header).
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [conversationId, setConversationId] = useState<string | null>(null);
  // The per-minute limit's Retry-After, counted down beside Ring now (ST-RV2).
  const wait = useDialWait();
  // plan-017 (Q5, OQ-1): Ring now dials from a caller-ID number on the organization's own trunk, so it is priced at
  // the trunk's outbound rate — named under the billing sentence; nothing while the rates are not read.
  const talkRate = useTalkRates();
  const trunkOutbound = talkRate.data?.talkRates.find((one) => one.kind === "sip_trunk_outbound");

  const chosenTrunk = sipTrunk.find((one) => one.number.some((number) => number.phoneNumberId === callerId));

  const start = useMutation({
    mutationFn: () => {
      const e164 = partyNumberOf(partyNumber);
      if (e164 === null) throw new Error(portalText("operations", "the_number_to_ring_could_not_be_read_623dc9f4"));
      const trimmed = reference.trim();
      return gateway.startOutboundConversation(voiceAgentId, {
        partyNumber: e164,
        callerIdPhoneNumberId: callerId,
        ringSecond: Number(ringSecond),
        ...(trimmed === "" ? {} : { reference: trimmed }),
        idempotencyKey,
      });
    },
    onSuccess: (started) => {
      setConversationId(started.conversationId);
    },
    onError: (raised: unknown) => {
      const second = dialWaitOf(raised);
      if (second === null) {
        setProblem(() => (ringRefusalOf(raised)));
      } else {
        setProblem(null);
        wait.start(second);
      }
      if (decided(raised)) setIdempotencyKey(crypto.randomUUID());
    },
  });

  const progress = useQuery({
    queryKey: ["outbound-conversation", conversationId ?? ""],
    queryFn: () => {
      if (conversationId === null) throw new Error(portalText("operations", "no_conversation_is_ringing_46637439"));
      return gateway.getOutboundConversation(conversationId);
    },
    enabled: conversationId !== null,
    refetchInterval: (query) => (query.state.data?.state === "ended" ? false : pollMs),
  });

  const end = useMutation({
    mutationFn: () => (conversationId === null ? Promise.resolve() : gateway.endOutboundConversation(conversationId)),
    onSettled: async () => {
      await progress.refetch();
    },
  });

  const close = (): void => {
    if (conversationId !== null) {
      // The conversation belongs in History and on the trunk's activity from now on.
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY.pastConversation });
      void queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunkEvent });
    }
    onClose();
  };

  const ring = (): void => {
    const partyProblem = partyNumberProblem(partyNumber, chosenTrunk?.allowedDestinationCountry ?? []);
    if (partyProblem !== null) {
      setProblem(() => ({ field: "party_number", get text() { return partyNumberProblem(partyNumber, chosenTrunk?.allowedDestinationCountry ?? [])!; } }));
      return;
    }
    const ringProblem = ringSecondProblem(ringSecond);
    if (ringProblem !== null) {
      setProblem(() => ({ field: "ring_second", get text() { return ringSecondProblem(ringSecond)!; } }));
      return;
    }
    if (reference.trim().length > REFERENCE_MAX) {
      setProblem(() => ({ field: "reference", get text() { return portalText("operations", "keep_the_reference_to_value_ordinary_characters_476d9896", { v0: String(String(REFERENCE_MAX)) }); } }));
      return;
    }
    setProblem(null);
    start.mutate();
  };

  const running = progress.data !== undefined && progress.data.state !== "ended";
  const chosen = choice.find((one) => one.phoneNumberId === callerId);

  return (
    <Dialog
      open
      title={portalText("operations", "ring_a_number_now_value_96ebbfb2", { v0: String(voiceAgentName) })}
      onClose={close}
      actions={
        <>
          {conversationId !== null && (progress.data === undefined || running) ? (
            <Button
              variant="secondary"
              disabled={end.isPending}
              onClick={() => {
                end.mutate();
              }}
            >
              {portalText("operations", "end_a2bb9d34")}</Button>
          ) : null}
          <Button variant="secondary" onClick={close}>
            {portalText("operations", "close_bbfa773e")}</Button>
          {conversationId === null ? (
            <Button
              variant="primary"
              disabled={start.isPending || choice.length === 0 || wait.remainingSecond !== null}
              onClick={ring}
            >
              {start.isPending ? portalText("operations", "ringing_417814b1") : portalText("operations", "ring_now_dccde773")}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="rn-form">
        {conversationId === null ? (
          <>
            <Field
              label={portalText("operations", "number_to_ring_8cf9f206")}
              description={portalText("operations", "a_greek_number_like_69_1234_5678_or_30_21_0123_4567_6c380958")}
              {...fieldError(problem, "party_number")}
            >
              {(control) => (
                <Input
                  {...control}
                  font="mono"
                  inputMode="tel"
                  autoComplete="off"
                  value={partyNumber}
                  onChange={(event) => {
                    setPartyNumber(event.currentTarget.value);
                  }}
                />
              )}
            </Field>
            <Field label={portalText("operations", "call_from_9047baac")} {...fieldError(problem, "caller_id_phone_number_id")}>
              {(control) => (
                <Select
                  {...control}
                  value={callerId}
                  onChange={(event) => {
                    setCallerId(event.currentTarget.value);
                  }}
                >
                  {choice.map((one) => (
                    <option key={one.phoneNumberId} value={one.phoneNumberId}>
                      {one.e164} · {one.sipTrunkDisplayName}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="rn-form-row">
              <Field
                label={portalText("operations", "ring_for_seconds_5fce496c")}
                description={portalText("operations", "value_to_value_seconds_1a8e05c4", { v0: String(String(RING_SECOND_MIN)), v1: String(String(RING_SECOND_MAX)) })}
                {...fieldError(problem, "ring_second")}
              >
                {(control) => (
                  <Input
                    {...control}
                    inputMode="numeric"
                    value={ringSecond}
                    onChange={(event) => {
                      setRingSecond(event.currentTarget.value);
                    }}
                  />
                )}
              </Field>
              <Field
                label={portalText("operations", "your_reference_optional_42267869")}
                description={portalText("operations", "shown_in_history_never_told_to_the_person_you_ring_f65cafae")}
                {...fieldError(problem, "reference")}
              >
                {(control) => (
                  <Input
                    {...control}
                    maxLength={REFERENCE_MAX}
                    value={reference}
                    onChange={(event) => {
                      setReference(event.currentTarget.value);
                    }}
                  />
                )}
              </Field>
            </div>
            <p className="c-note">{RING_BILLING_SENTENCE()}</p>
            {trunkOutbound === undefined ? null : (
              <p className="c-note">{outboundRateTextOf(trunkOutbound.kind, trunkOutbound.perMinute)}</p>
            )}
          </>
        ) : (
          <>
            <p className="c-note">
              {partyNumberOf(partyNumber) ?? partyNumber} {portalText("operations", "from_b9426d12")}{chosen?.e164 ?? portalText("operations", "your_trunk_bc73a720")}
            </p>
            <p className="rn-progress" role="status">
              {progress.data === undefined ? portalText("operations", "ringing_417814b1") : ringProgressWords(progress.data)}
            </p>
            {progress.data !== undefined && progress.data.answeredAtText !== null ? (
              <p className="c-note">{HD_SENTENCE()}</p>
            ) : null}
            {running ? <p className="c-note">{portalText("operations", "closing_this_window_does_not_hang_up_use_end_1c1600bf")}</p> : null}
            {progress.isError ? (
              <p className="c-note c-note-critical" role="alert">
                {apiErrorText(progress.error, portalText("operations", "how_the_conversation_is_going_could_not_be_read_c2f030a1"))} {portalText("operations", "it_may_still_be_ringing_look_in_history_e11f2a65")}</p>
            ) : null}
            {end.isError ? (
              <p className="c-note c-note-critical" role="alert">
                {apiErrorText(end.error, portalText("operations", "the_conversation_could_not_be_ended_7883ba68"))}
              </p>
            ) : null}
          </>
        )}
        {problem !== null && problem.field === null ? (
          <p className="c-note c-note-critical" role="alert">
            {problem.text}
          </p>
        ) : null}
        {conversationId === null && wait.remainingSecond !== null ? (
          <p className="c-note c-note-critical" role="alert">
            {dialRateLimitedSentence(wait.remainingSecond)}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
